<script setup lang="ts">
import { RunMeasurementRequestDto, TaskItemReferenceMeasurementResultsDto } from '@/api/generator';
import { ref, watch } from 'vue';
import { Measurement } from './types';
import { api } from '@/api';
import { hasUnacceptableErrors } from './utils/useMeasurement';
import MeasurementDialog from './MeasurementDialog.vue';
import MeasurementStatus from './MeasurementStatus.vue';
import ParameterTable from '../ParameterTable/ParameterTable.vue';
import ReferenceMeasurementResults from '@/views/tasks/components/ReferenceMeasurementResults.vue';

const props = defineProps<{
  open: boolean;
  measurement?: Measurement;
}>();

const emits = defineEmits<{
  'update:open': [open: boolean];
  accepted: [];
}>();

const measurementId = ref<string | undefined>(undefined);

const result = ref<TaskItemReferenceMeasurementResultsDto | undefined>(undefined);

function reset() {
  result.value = undefined;
  measurementId.value = undefined;
}

function toggleDialog(open?: boolean) {
  emits('update:open', open === undefined ? !props.open : open);
}

async function acceptReferenceMeasurement(request?: RunMeasurementRequestDto) {
  if (measurementId.value && request) {
    const { success } = await api.taskItemsApi.taskItemAcceptReferenceMeasurement({
      acceptReferenceMeasurementDto: {
        measurementId: measurementId.value,
        $type:
          'Ptw.Ptw100.AppZero.Application.Shared.TaskItemComponent.Dto.AcceptReferenceMeasurementDto, Ptw.Ptw100.AppZero.Application.Shared',
      },
    });

    if (success) {
      result.value = undefined;
      toggleDialog(false);
      emits('accepted');
    }
  }
}

async function getReferenceMeasurementResult(id: string) {
  const { result: referenceMeasurementResult } =
    await api.taskItemsApi.getTaskItemReferenceMeasurementResults({
      measurementId: id,
    });

  return referenceMeasurementResult;
}

async function onFinished(id: string) {
  result.value = await getReferenceMeasurementResult(id);
  measurementId.value = id;
}

watch(
  () => props.open,
  () => reset(),
);
</script>

<template>
  <MeasurementDialog
    v-if="open"
    :open="open"
    :title="$t('ReferenceMeasurementDialogTitle')"
    :measurements="measurement ? [measurement] : undefined"
    @measurement:finished="onFinished"
    @update:open="toggleDialog"
  >
    <template #instructions>
      <p class="instructions mt-4">
        {{ $t('ReferenceMeasurementInstruction') }}
      </p>
    </template>

    <template #measurements="{ status }">
      <v-row>
        <v-col>
          <MeasurementStatus :status="status.value?.measurementStatus ?? undefined" class="mb-4" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <ParameterTable
            :title="$t('TargetSystemParameter_other')"
            :parameters="measurement?.setup?.targetSystem"
          />
        </v-col>

        <v-col>
          <ParameterTable
            :title="$t('SupportSystemParameter_other')"
            :parameters="measurement?.setup?.supportSystem"
          />
        </v-col>
      </v-row>

      <v-divider v-if="result" class="mt-4 mb-6" />

      <ReferenceMeasurementResults :result="result" />
    </template>

    <template #actions="{ repeat, isRepeatable, request, finished, messages }">
      <SecondaryVBtn
        v-if="isRepeatable"
        :text="$t('Repeat')"
        @click="
          reset();
          repeat();
        "
      />

      <PrimaryVBtn
        v-if="finished"
        class="ml-4"
        :text="$t('Accept')"
        :disabled="hasUnacceptableErrors(messages.value)"
        @click="acceptReferenceMeasurement(request.value)"
      />
    </template>
  </MeasurementDialog>
</template>

<style scoped lang="scss">
.instructions {
  white-space: pre-wrap;
  width: calc(100% - 100px);
}
</style>
